import React, { useState, useEffect, useRef, useMemo } from 'react';
import { View, Class, Student, Homework, UserSettings, Assignment, GradingSystem } from '../../types';
import { 
    XMarkIcon, 
    CheckIcon, 
    InformationCircleIcon, 
    PlusIcon, 
    ArrowPathIcon, 
    MagnifyingGlassIcon, 
    PencilSquareIcon, 
    TrashIcon, 
    ArrowUpTrayIcon, 
    ExclamationTriangleIcon,
    ClipboardDocumentListIcon,
    ClipboardDocumentCheckIcon,
    MegaphoneIcon as MegaphoneIconSolid,
    EyeIcon,
    PlayIcon,
    UserGroupIcon
} from './Icons';
import Spinner from './Spinner';
import { uploadSubmissionAttachment, formatAssetUrl } from '../../services/apiService';
import { MindmapAttachSelect } from '../../features/mindmap';
import type { MindmapAttachment } from '../../features/mindmap/types';

interface AssignHomeworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  homeworks: Homework[];
  classStudents: Student[];
  classes: Class[];
  classToAssign?: string | null;
  onAssign: (assignment: Partial<Assignment> & { isPinned?: boolean }) => void;
  initialType?: 'homework' | 'task' | 'announcement';
  onNavigate?: (view: View, payload?: object) => void;
  user: UserSettings;
  editingAssignment?: Assignment | null;
  isSaving?: boolean;
  resourceInfo?: {
      id: string;
      title: string;
      type: 'video' | 'homework' | 'document';
      thumbnail?: string;
  };
}

export const AssignHomeworkModal: React.FC<AssignHomeworkModalProps> = ({ 
    isOpen, onClose, homeworks, classStudents, classes, classToAssign, onAssign, isSaving, initialType, onNavigate, user, editingAssignment, resourceInfo 
}) => {
    const [type, setType] = useState<'homework' | 'task' | 'announcement'>('homework');
    const [selectedHomeworkId, setSelectedHomeworkId] = useState('');
    const [selectedClassId, setSelectedClassId] = useState(classToAssign || '');
    const [title, setTitle] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [scheduledDate, setScheduledDate] = useState('');
    const [allowLateSubmission, setAllowLateSubmission] = useState(true);
    const [maxAttempts, setMaxAttempts] = useState(1);
    const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | ''>('');
    const [passingGrade, setPassingGrade] = useState(5);
    const [isRequired, setIsRequired] = useState(true);
    const [instructions, setInstructions] = useState('');
    const [submissionType, setSubmissionType] = useState<'none' | 'check_only' | 'photo' | 'audio_video' | 'any'>('check_only');
    const [gradingSystem, setGradingSystem] = useState<GradingSystem>('numeric');
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    const [assignToAll, setAssignToAll] = useState(true);
    const [isPinned, setIsPinned] = useState(false);
    const [taskAttachments, setTaskAttachments] = useState<{file: File, url?: string, type: string, name: string}[]>([]);
    const [mindmapAttachment, setMindmapAttachment] = useState<MindmapAttachment | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [homeworkSearchQuery, setHomeworkSearchQuery] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const selectedClass = useMemo(
        () => (classes || []).find(c => String(c.id) === String(selectedClassId)) || null,
        [classes, selectedClassId]
    );

    /**
     * Điểm khớp giữa bài tập và lớp đã chọn — dùng để ưu tiên gợi ý học liệu
     * đúng môn + cấp của lớp (lớp gắn môn: THCS/THPT GV bộ môn dạy 1 môn nhiều lớp).
     * Môn khớp = 2 điểm (so khoan dung: bỏ phân môn "(...)", không phân biệt hoa thường),
     * cấp học khớp = 1 điểm. Chỉ sắp thứ tự, không loại bài nào — GV vẫn chọn được tất cả.
     */
    const homeworkMatchScore = useMemo(() => {
        const map = new Map<string, number>();
        if (!selectedClass) return map;
        const norm = (s?: string) => (s || '').replace(/\s*\(.+\)\s*$/, '').trim().toLowerCase();
        const clsSubject = norm(selectedClass.subject);
        homeworks.forEach(hw => {
            let score = 0;
            if (clsSubject && norm((hw as any).subject) === clsSubject) score += 2;
            if (selectedClass.gradeLevel && (hw as any).gradeLevel === selectedClass.gradeLevel) score += 1;
            map.set(String(hw.id), score);
        });
        return map;
    }, [homeworks, selectedClass]);

    const filteredHomeworks = useMemo(() => {
        const score = (hw: Homework) => homeworkMatchScore.get(String(hw.id)) ?? 0;
        const sortByMatch = (list: Homework[]) => [...list].sort((a, b) => score(b) - score(a));
        if (!homeworkSearchQuery.trim()) return sortByMatch(homeworks);
        const q = homeworkSearchQuery.toLowerCase().trim();
        const filtered = sortByMatch(homeworks.filter(hw => (hw.title || '').toLowerCase().includes(q)));
        const selected = homeworks.find(h => String(h.id) === selectedHomeworkId);
        if (selected && !filtered.some(f => String(f.id) === String(selected.id))) {
            return [selected, ...filtered];
        }
        return filtered;
    }, [homeworks, homeworkSearchQuery, selectedHomeworkId, homeworkMatchScore]);

    useEffect(() => {
        if (isOpen) {
            if (editingAssignment) {
                const currentType = editingAssignment.type || (editingAssignment.homeworkId ? 'homework' : 'task');
                setType(currentType as any);
                setSelectedHomeworkId(editingAssignment.homeworkId || '');
                setTitle(editingAssignment.title || '');
                setDueDate(editingAssignment.dueDate || '');
                setScheduledDate(editingAssignment.scheduledDate || '');
                setInstructions(editingAssignment.instructions || '');
                setAssignToAll(!editingAssignment.studentIds);
                setSelectedStudentIds(editingAssignment.studentIds || []);
                setIsRequired(editingAssignment.isRequired ?? true);
                setAllowLateSubmission(editingAssignment.allowLateSubmission ?? true);
                setSubmissionType(editingAssignment.submissionType || 'check_only');
                setMaxAttempts(editingAssignment.maxAttempts || 1);
                setTimeLimitMinutes(editingAssignment.timeLimitMinutes || '');
                setPassingGrade(editingAssignment.passingGrade || 5);
                setGradingSystem(editingAssignment.gradingSystem || 'numeric');
                setIsPinned(editingAssignment.isPinned || false);
                setTaskAttachments(editingAssignment.attachments?.map(att => ({ file: new File([], att.name), url: att.url, type: att.type, name: att.name })) || []);
                setMindmapAttachment((editingAssignment.mindmaps?.[0] as MindmapAttachment) || null);
            } else {
                if (initialType) {
                    setType(initialType);
                }
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0];
                setScheduledDate(todayStr);
                
                const nextWeek = new Date();
                nextWeek.setDate(today.getDate() + 7);
                setDueDate(nextWeek.toISOString().split('T')[0]);
                
                setAssignToAll(true);
                setSelectedStudentIds([]);
                setTaskAttachments([]);
                setMindmapAttachment(null);
                setTitle('');
                setInstructions('');
                setErrors({});
                setHomeworkSearchQuery('');

                if (classToAssign && classes) {
                    setSelectedClassId(classToAssign);
                    const cls = classes.find(c => String(c.id) === String(classToAssign));
                    setGradingSystem(cls?.gradingSystem || 'numeric');
                } else if (classes && classes.length > 0) {
                    // Mặc định chọn lớp đầu tiên nếu không có classToAssign
                    setSelectedClassId(String(classes[0].id));
                    setGradingSystem(classes[0].gradingSystem || 'numeric');
                }

                if (resourceInfo) {
                    setTitle(resourceInfo.title);
                }
            }
        }
    }, [isOpen, editingAssignment, initialType, classToAssign, classes]);

    const filteredStudents = useMemo(() => {
        if (!selectedClassId || !classes) return classStudents;
        const cls = classes.find(c => String(c.id) === String(selectedClassId));
        if (!cls || !cls.studentIds) return classStudents;
        return classStudents.filter(s => cls.studentIds.includes(String(s.id)));
    }, [selectedClassId, classes, classStudents]);

    const handleToggleStudent = (id: string | number) => {
        const normalizedId = String(id);
        setSelectedStudentIds(prev => 
            prev.includes(normalizedId) ? prev.filter(sid => sid !== normalizedId) : [...prev, normalizedId]
        );
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        
        const maxFiles = user.maxFilesPerAssignment || 5;
        const maxFileSizeMB = user.maxFileSizeMB || 10;

        if (taskAttachments.length + files.length > maxFiles) {
            alert(`Chỉ được đính kèm tối đa ${maxFiles} tệp.`);
            return;
        }

        const newAttachments = files.map(file => {
            if (file.size > maxFileSizeMB * 1024 * 1024) {
                alert(`Tệp "${file.name}" quá lớn (tối đa ${maxFileSizeMB}MB).`);
                return null;
            }
            return {
                file,
                type: file.type.startsWith('image/') ? 'image' : (file.type.startsWith('video/') ? 'video' : (file.type.startsWith('audio/') ? 'audio' : 'document')),
                name: file.name
            };
        }).filter(Boolean) as {file: File, type: string, name: string}[];

        setTaskAttachments(prev => [...prev, ...newAttachments]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeAttachment = (index: number) => {
        setTaskAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleAssign = async () => {
        const newErrors: Record<string, string> = {};
        
        if (!selectedClassId) {
            newErrors.selectedClassId = 'Vui lòng chọn lớp học.';
        }
        if (type === 'homework' && !selectedHomeworkId) {
            newErrors.selectedHomeworkId = 'Vui lòng chọn bài tập từ thư viện.';
        }
        if ((type === 'task' || type === 'announcement') && !title.trim()) {
            newErrors.title = 'Vui lòng nhập tiêu đề.';
        }
        if (type === 'announcement' && !instructions.trim()) {
            newErrors.instructions = 'Vui lòng nhập nội dung thông báo.';
        }
        if (type !== 'announcement' && !dueDate) {
            newErrors.dueDate = 'Vui lòng chọn hạn hoàn thành.';
        }
        if (type !== 'announcement' && dueDate && scheduledDate && new Date(dueDate) < new Date(scheduledDate)) {
            newErrors.dueDate = 'Hạn hoàn thành không thể trước ngày bắt đầu.';
        }
        if (!assignToAll && selectedStudentIds.length === 0) {
            newErrors.students = 'Vui lòng chọn ít nhất một học sinh nhận.';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        let uploadedAttachments: Assignment['attachments'] = [];
        
        if (taskAttachments.length > 0) {
            setIsUploading(true);
            try {
                const uploadPromises = taskAttachments.map(async (att) => {
                    if (att.url) return { name: att.name, url: att.url, type: att.type, size: att.file.size };
                    const res = await uploadSubmissionAttachment(att.file);
                    return { name: res.name, url: res.url, type: res.type, size: att.file.size };
                });
                uploadedAttachments = await Promise.all(uploadPromises);
            } catch (error) {
                alert('Lỗi khi tải tệp lên. Vui lòng thử lại.');
                setIsUploading(false);
                return;
            }
            setIsUploading(false);
        }

        const homework = type === 'homework' ? homeworks.find(hw => hw.id === selectedHomeworkId) : null;

        onAssign({
            id: editingAssignment?.id,
            type,
            title: type !== 'homework' ? title : (homework?.title || undefined),
            homeworkId: type === 'homework' ? selectedHomeworkId : undefined,
            dueDate: type !== 'announcement' ? dueDate : undefined,
            scheduledDate: scheduledDate || undefined,
            allowLateSubmission: type === 'announcement' ? undefined : allowLateSubmission,
            maxAttempts: type === 'homework' ? maxAttempts : undefined,
            timeLimitMinutes: (type === 'homework' && timeLimitMinutes !== '') ? timeLimitMinutes : undefined,
            passingGrade: type === 'homework' ? passingGrade : undefined,
            isRequired: type === 'announcement' ? false : isRequired,
            instructions,
            submissionType: type === 'task' ? submissionType : undefined,
            gradingSystem,
            studentIds: assignToAll ? undefined : selectedStudentIds,
            status: scheduledDate ? 'scheduled' : 'active',
            isPinned: type === 'announcement' ? isPinned : undefined,
            attachments: uploadedAttachments.length > 0 ? uploadedAttachments : (editingAssignment?.attachments || undefined),
            mindmaps: mindmapAttachment ? [mindmapAttachment] : [],
            classId: selectedClassId || undefined
        });
    };

    if (!isOpen) return null;

    return (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto py-10" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full m-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center bg-teal-600 text-white rounded-t-2xl">
                    <h3 className="text-xl font-bold">
                        {resourceInfo?.type === 'video' ? 'Giao Video tương tác' : (type === 'homework' ? 'Giao bài tập' : type === 'task' ? 'Giao nhiệm vụ' : 'Tạo thông báo')}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors"><XMarkIcon className="h-6 w-6 text-white" /></button>
                </div>
                
                <div className="p-6 space-y-6 overflow-y-auto">
                    {resourceInfo && (
                        <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-2xl flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="w-16 h-16 bg-indigo-100 rounded-xl flex items-center justify-center shrink-0">
                                {resourceInfo.thumbnail ? (
                                    <img src={resourceInfo.thumbnail} alt="Thumbnail" className="w-full h-full object-cover rounded-xl" />
                                ) : (
                                    resourceInfo.type === 'video' ? <PlayIcon className="h-8 w-8 text-indigo-600" /> : <ClipboardDocumentListIcon className="h-8 w-8 text-indigo-600" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Đang gắn với {resourceInfo.type === 'video' ? 'Video tương tác' : 'Tài nguyên'}</p>
                                <h4 className="text-sm font-bold text-indigo-900 truncate">{resourceInfo.title}</h4>
                                <p className="text-[10px] text-indigo-500 italic mt-0.5">Học sinh sẽ xem video và thực hiện các tương tác bạn đã cài đặt.</p>
                            </div>
                        </div>
                    )}

                    {!initialType && (
                        <div className="grid grid-cols-3 gap-3">
                            <button 
                                type="button"
                                onClick={() => setType('homework')}
                                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all ${type === 'homework' ? 'border-teal-500 bg-teal-50' : 'border-slate-100 hover:border-slate-200'}`}
                            >
                                <ClipboardDocumentListIcon className={`h-6 w-6 ${type === 'homework' ? 'text-teal-600' : 'text-slate-400'}`} />
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${type === 'homework' ? 'text-teal-700' : 'text-slate-500'}`}>Bài tập</span>
                            </button>
                            <button 
                                type="button"
                                onClick={() => setType('task')}
                                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all ${type === 'task' ? 'border-teal-500 bg-teal-50' : 'border-slate-100 hover:border-slate-200'}`}
                            >
                                <ClipboardDocumentCheckIcon className={`h-6 w-6 ${type === 'task' ? 'text-teal-600' : 'text-slate-400'}`} />
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${type === 'task' ? 'text-teal-700' : 'text-slate-500'}`}>Nhiệm vụ</span>
                            </button>
                            <button 
                                type="button"
                                onClick={() => setType('announcement')}
                                className={`flex flex-col items-center gap-2 p-3 rounded-2xl border-2 transition-all ${type === 'announcement' ? 'border-teal-500 bg-teal-50' : 'border-slate-100 hover:border-slate-200'}`}
                            >
                                <MegaphoneIconSolid className={`h-6 w-6 ${type === 'announcement' ? 'text-teal-600' : 'text-slate-400'}`} />
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${type === 'announcement' ? 'text-teal-700' : 'text-slate-500'}`}>Thông báo</span>
                            </button>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Cột trái: Thông tin cơ bản */}
                        <div className="space-y-4">
                            {/* Chọn lớp học (Chỉ hiện nếu chưa có classToAssign cố định) */}
                            {!classToAssign && classes && classes.length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 mb-1">Giao cho lớp học <span className="text-rose-500">*</span></label>
                                    <div className="relative">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                            <UserGroupIcon className="h-4 w-4" />
                                        </div>
                                        <select 
                                            value={selectedClassId} 
                                            onChange={e => {
                                                const cid = e.target.value;
                                                setSelectedClassId(cid);
                                                const cls = classes.find(c => String(c.id) === String(cid));
                                                if (cls) setGradingSystem(cls.gradingSystem || 'numeric');
                                                setErrors(prev => ({ ...prev, selectedClassId: '' }));
                                            }}
                                            className={`w-full pl-9 pr-3 py-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 transition-all text-sm font-medium ${errors.selectedClassId ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`}
                                        >
                                            <option value="">-- Chọn lớp học --</option>
                                            {classes.map(cls => (
                                                <option key={cls.id} value={cls.id}>{cls.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {errors.selectedClassId && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.selectedClassId}</p>}
                                </div>
                            )}

                            {type === 'homework' ? (
                                <div>
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <label className="text-xs font-semibold text-slate-500">Chọn bài tập từ thư viện <span className="text-rose-500">*</span></label>
                                        {onNavigate && (
                                            <button
                                                type="button"
                                                onClick={() => { onClose(); onNavigate(View.HomeworkCreator); }}
                                                className="text-xs font-medium text-teal-600 hover:text-teal-700 hover:underline"
                                            >
                                                Tạo bài tập
                                            </button>
                                        )}
                                    </div>
                                    {homeworks.length > 5 && (
                                        <div className="relative mb-2">
                                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                            <input
                                                type="text"
                                                value={homeworkSearchQuery}
                                                onChange={e => setHomeworkSearchQuery(e.target.value)}
                                                placeholder="Tìm kiếm bài tập theo tên..."
                                                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                                            />
                                        </div>
                                    )}
                                    <select 
                                        value={selectedHomeworkId} 
                                        onChange={e => { setSelectedHomeworkId(e.target.value); setErrors(prev => ({ ...prev, selectedHomeworkId: '' })); }} 
                                        className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 transition-all text-sm font-medium ${errors.selectedHomeworkId ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`}
                                    >
                                        <option value="">-- Chọn bài tập --</option>
                                        {filteredHomeworks.map(hw => (
                                            <option key={hw.id} value={hw.id}>
                                                {(homeworkMatchScore.get(String(hw.id)) ?? 0) >= 2 ? '⭐ ' : ''}{hw.title}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedClass?.subject && (
                                        <p className="mt-1 text-[10px] text-teal-600 font-medium">
                                            ⭐ Ưu tiên hiển thị bài tập môn <strong>{selectedClass.subject}</strong> của lớp {selectedClass.name}.
                                        </p>
                                    )}
                                    {homeworkSearchQuery.trim() && filteredHomeworks.length === 0 && (
                                        <p className="mt-1 text-xs text-slate-500">Không tìm thấy bài tập phù hợp.</p>
                                    )}
                                    {errors.selectedHomeworkId && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.selectedHomeworkId}</p>}
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-xs font-semibold text-slate-500 mb-1">Tiêu đề {type === 'task' ? 'nhiệm vụ' : 'thông báo'} <span className="text-rose-500">*</span></label>
                                    <input 
                                        type="text" 
                                        value={title} 
                                        onChange={e => { setTitle(e.target.value); setErrors(prev => ({ ...prev, title: '' })); }} 
                                        placeholder={type === 'task' ? "Ví dụ: Hoàn thành bài tập trang 15 sách giáo khoa" : "Ví dụ: Thông báo họp phụ huynh đột xuất"}
                                        className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 transition-all text-sm font-medium ${errors.title ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} 
                                    />
                                    {errors.title && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.title}</p>}
                                </div>
                            )}
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div className={type === 'announcement' ? 'col-span-2' : ''}>
                                    <label className="block text-xs font-semibold text-slate-500 mb-1">{type === 'announcement' ? 'Ngày đăng' : 'Ngày bắt đầu'}</label>
                                    <input 
                                        type="date" 
                                        value={scheduledDate} 
                                        onChange={e => { setScheduledDate(e.target.value); setErrors(prev => ({ ...prev, dueDate: '' })); }} 
                                        className="w-full p-3 bg-slate-50 border-2 border-slate-100 rounded-xl focus:border-teal-500 transition-all text-sm font-medium" 
                                        placeholder="Giao ngay"
                                    />
                                </div>
                                {type !== 'announcement' && (
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-500 mb-1">Hạn hoàn thành <span className="text-rose-500">*</span></label>
                                        <input 
                                            type="date" 
                                            value={dueDate} 
                                            onChange={e => { setDueDate(e.target.value); setErrors(prev => ({ ...prev, dueDate: '' })); }} 
                                            className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 transition-all text-sm font-medium ${errors.dueDate ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`} 
                                        />
                                        {errors.dueDate && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.dueDate}</p>}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-500 mb-1">{type === 'announcement' ? 'Nội dung thông báo' : 'Hướng dẫn chi tiết'} {type === 'announcement' && <span className="text-rose-500">*</span>}</label>
                                <textarea 
                                    value={instructions} 
                                    onChange={e => { setInstructions(e.target.value); setErrors(prev => ({ ...prev, instructions: '' })); }} 
                                    className={`w-full p-3 bg-slate-50 border-2 rounded-xl focus:border-teal-500 transition-all text-sm h-32 outline-none resize-none font-medium ${errors.instructions ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`}
                                    placeholder={type === 'announcement' ? "Nhập nội dung chi tiết gửi đến phụ huynh và học sinh..." : "Nhập hướng dẫn thực hiện nhiệm vụ..."}
                                />
                                {errors.instructions && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.instructions}</p>}
                            </div>

                            {type !== 'announcement' && (
                                <div className="pt-2 p-3 bg-violet-50/40 border-2 border-violet-100 rounded-xl">
                                    <MindmapAttachSelect value={mindmapAttachment} onChange={setMindmapAttachment} />
                                </div>
                            )}

                            <div className="space-y-3 pt-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tệp đính kèm (tùy chọn)</label>
                                    <button 
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="flex items-center gap-1.5 text-[10px] font-black text-teal-600 uppercase hover:text-teal-700 transition-colors"
                                    >
                                        <PlusIcon className="h-3 w-3" /> Thêm tệp
                                    </button>
                                </div>
                                
                                <input 
                                    type="file" 
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    multiple
                                    className="hidden"
                                />

                                {taskAttachments.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {taskAttachments.map((att, idx) => {
                                            const previewUrl = att.url ? formatAssetUrl(att.url) : (att.file && att.file.size > 0 ? URL.createObjectURL(att.file) : null);
                                            return (
                                                <div key={idx} className="relative group bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 pr-8 animate-in fade-in zoom-in duration-200">
                                                    <div 
                                                        className={`flex items-center gap-2 ${previewUrl ? 'cursor-pointer hover:text-teal-600 transition-colors' : ''}`}
                                                        onClick={() => {
                                                            if (!previewUrl) return;
                                                            if (att.type === 'image') setZoomImage(previewUrl);
                                                            else window.open(previewUrl, '_blank');
                                                        }}
                                                        title={previewUrl ? "Nhấn để xem trước" : ""}
                                                    >
                                                        <span className="text-[14px]">
                                                            {att.type === 'image' ? '🖼' : att.type === 'video' ? '🎬' : att.type === 'audio' ? '🎵' : '📄'}
                                                        </span>
                                                        <div className="min-w-0">
                                                            <p className="text-[10px] font-bold text-slate-700 truncate max-w-[120px]">{att.name}</p>
                                                            <p className="text-[8px] text-slate-400 uppercase font-black tracking-tighter">
                                                                {att.file?.size ? (att.file.size / 1024 / 1024).toFixed(1) + ' MB' : (att.url ? 'Sẵn có' : 'Đang chờ')}
                                                            </p>
                                                        </div>
                                                        {previewUrl && (
                                                            <div className="absolute inset-0 bg-teal-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl pointer-events-none flex items-center justify-end pr-8 gap-1">
                                                                <span className="text-[8px] font-black text-teal-600 uppercase tracking-tighter">Xem</span>
                                                                <EyeIcon className="h-3 w-3 text-teal-600" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <button 
                                                        type="button"
                                                        onClick={() => removeAttachment(idx)}
                                                        className="absolute top-1/2 -translate-y-1/2 right-2 p-1 bg-white text-rose-500 rounded-lg shadow-sm border border-rose-100 opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-50"
                                                    >
                                                        <XMarkIcon className="h-3 w-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div 
                                        onClick={() => fileInputRef.current?.click()}
                                        className="border-2 border-dashed border-slate-100 rounded-2xl py-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-teal-100 hover:bg-teal-50/30 transition-all group"
                                    >
                                        <ArrowUpTrayIcon className="h-6 w-6 text-slate-300 group-hover:text-teal-400 transition-colors" />
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest group-hover:text-teal-600">Nhấn để tải tệp lên</p>
                                    </div>
                                )}
                                <p className="text-[9px] text-slate-400 italic">Hỗ trợ ảnh, video, tài liệu. Tối đa {user.maxFilesPerAssignment || 5} tệp, mỗi tệp không quá {user.maxFileSizeMB || 10}MB.</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {type === 'homework' && (
                                <div className="p-4 bg-slate-50 rounded-2xl border-2 border-slate-100 space-y-4">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cấu hình bài tập</h4>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-slate-600">Số lần làm tối đa</span>
                                        <input type="number" min="1" value={maxAttempts} onChange={e => setMaxAttempts(parseInt(e.target.value) || 1)} className="w-16 p-1 text-xs border rounded" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-medium text-slate-600">Thời gian (phút)</span>
                                        <input type="number" placeholder="Vô hạn" value={timeLimitMinutes} onChange={e => setTimeLimitMinutes(e.target.value === '' ? '' : parseInt(e.target.value))} className="w-20 p-1 text-xs border rounded" />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex justify-between text-xs font-medium text-slate-600">
                                            <span>Điểm đạt</span>
                                            <span className="text-teal-600 font-bold">{passingGrade}/10</span>
                                        </div>
                                        <input 
                                            type="range" 
                                            min={0} 
                                            max={10} 
                                            step={0.5}
                                            value={passingGrade} 
                                            onChange={e => setPassingGrade(parseFloat(e.target.value))} 
                                            className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                                        />
                                    </div>
                                </div>
                            )}

                            {type !== 'announcement' ? (
                                <div className="space-y-4 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{type === 'task' ? 'Hình thức hoàn thành & Quy tắc' : 'Quy tắc nộp bài'}</h4>
                                    
                                    {type === 'task' && (
                                        <div className="space-y-2">
                                            <p className="text-[10px] font-bold text-slate-500 uppercase">Yêu cầu phản hồi từ PH/HS</p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setSubmissionType('none')}
                                                    className={`px-3 py-2 rounded-xl border-2 text-[11px] font-bold transition-all ${submissionType === 'none' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-white bg-white text-slate-500 hover:border-slate-200'}`}
                                                >
                                                    Chỉ xem
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setSubmissionType('check_only')}
                                                    className={`px-3 py-2 rounded-xl border-2 text-[11px] font-bold transition-all ${submissionType === 'check_only' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-white bg-white text-slate-500 hover:border-slate-200'}`}
                                                >
                                                    Xác nhận xong
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setSubmissionType('photo')}
                                                    className={`px-3 py-2 rounded-xl border-2 text-[11px] font-bold transition-all ${submissionType === 'photo' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-white bg-white text-slate-500 hover:border-slate-200'}`}
                                                >
                                                    Nộp ảnh
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setSubmissionType('audio_video')}
                                                    className={`px-3 py-2 rounded-xl border-2 text-[11px] font-bold transition-all ${submissionType === 'audio_video' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-white bg-white text-slate-500 hover:border-slate-200'}`}
                                                >
                                                    Video/Âm thanh
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setSubmissionType('any')}
                                                    className={`px-3 py-2 rounded-xl border-2 text-[11px] font-bold transition-all ${submissionType === 'any' ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-white bg-white text-slate-500 hover:border-slate-200'}`}
                                                >
                                                    Bất kỳ tệp
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex flex-col gap-2 pt-1">
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                            <input type="checkbox" checked={allowLateSubmission} onChange={e => setAllowLateSubmission(e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" />
                                            <span className="text-sm font-medium text-slate-600 group-hover:text-teal-600 transition-colors">Cho phép nộp muộn</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                            <input type="checkbox" checked={isRequired} onChange={e => setIsRequired(e.target.checked)} className="rounded text-teal-600 focus:ring-teal-500" />
                                            <span className="text-sm font-medium text-slate-600 group-hover:text-teal-600 transition-colors">Bắt buộc hoàn thành</span>
                                        </label>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3 p-4 bg-slate-50/50 rounded-2xl border border-slate-100">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Cấu hình thông báo</h4>
                                    <div className="flex flex-col gap-2">
                                        <label className="flex items-center gap-2 cursor-pointer group">
                                            <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} className="rounded text-sky-600 focus:ring-sky-500" />
                                            <span className="text-sm font-medium text-slate-600 group-hover:text-sky-600 transition-colors">Ghim thông báo lên đầu</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {type !== 'announcement' && (
                                <div className="p-4 bg-sky-50 border border-sky-100 rounded-2xl space-y-3">
                                    <div className="flex items-center justify-between">
                                        <label className="text-xs font-black text-sky-700 uppercase tracking-widest">Kiểu chấm điểm</label>
                                        <div className="group relative">
                                            <InformationCircleIcon className="h-4 w-4 text-sky-300 cursor-help hover:text-sky-500 transition-colors" />
                                            <div className="absolute right-0 bottom-full mb-2 w-64 p-3 bg-slate-800 text-white text-[11px] rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all pointer-events-none z-50 leading-relaxed font-normal whitespace-normal">
                                                <p className="mb-1"><span className="text-sky-400 font-bold">Hệ 10:</span> Điểm số từ 0.0 đến 10.0.</p>
                                                <p className="mb-1"><span className="text-sky-400 font-bold">Hệ chữ:</span> Tự động quy đổi sang A, B, C, D, F.</p>
                                                <p className="mb-1"><span className="text-sky-400 font-bold">Đạt/Không đạt:</span> Điểm {'>'}= 5.0 là Đạt.</p>
                                                <p><span className="text-teal-400 font-bold">Không chấm:</span> Chỉ gửi lời phê & nhận xét khích lệ.</p>
                                                <div className="absolute top-full right-2 border-8 border-transparent border-t-slate-800"></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                        {[
                                            { val: 'numeric', label: 'Hệ 10' },
                                            { val: 'letter', label: 'Hệ chữ' },
                                            { val: 'pass_fail', label: 'Đạt/Không' },
                                            { val: 'none', label: 'Không chấm' },
                                        ].map(opt => (
                                            <button
                                                key={opt.val}
                                                type="button"
                                                onClick={() => setGradingSystem(opt.val as GradingSystem)}
                                                className={`py-2 px-1 rounded-xl border-2 text-[10px] font-black uppercase tracking-tight transition-all ${gradingSystem === opt.val ? 'border-sky-500 bg-white text-sky-700 ring-2 ring-sky-100' : 'border-transparent bg-white/50 text-slate-400 hover:border-sky-200'}`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-semibold text-slate-500 mb-1">Người nhận</label>
                                <div className="flex bg-slate-100 p-1 rounded-xl mb-3">
                                    <button 
                                        onClick={() => setAssignToAll(true)}
                                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${assignToAll ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        Cả lớp
                                    </button>
                                    <button 
                                        onClick={() => setAssignToAll(false)}
                                        className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${!assignToAll ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                                    >
                                        Chọn học sinh
                                    </button>
                                </div>

                                {!assignToAll && (
                                    <div className={`max-h-40 overflow-y-auto p-2 bg-slate-50 rounded-xl border-2 transition-all space-y-1 ${errors.students ? 'border-rose-300 bg-rose-50/30' : 'border-slate-100'}`}>
                                        {filteredStudents.map(s => (
                                            <label key={s.id} className="flex items-center gap-2 p-2 hover:bg-white rounded-lg cursor-pointer transition-colors group">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedStudentIds.includes(String(s.id))} 
                                                    onChange={() => { handleToggleStudent(s.id); setErrors(prev => ({ ...prev, students: '' })); }}
                                                    className="rounded text-teal-600 focus:ring-teal-500" 
                                                />
                                                <span className="text-sm font-medium text-slate-700 group-hover:text-teal-600">{s.name}</span>
                                            </label>
                                        ))}
                                        {filteredStudents.length === 0 && (
                                            <p className="text-center py-4 text-xs text-slate-400 italic">Lớp học này chưa có học sinh.</p>
                                        )}
                                    </div>
                                )}
                                {errors.students && <p className="mt-1 text-[10px] text-rose-600 font-bold">{errors.students}</p>}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 rounded-b-2xl border-t">
                    <button onClick={onClose} disabled={isSaving || isUploading} className="px-6 py-2 bg-white border border-slate-200 text-slate-500 font-bold text-sm rounded-xl hover:bg-slate-100 transition-all">Hủy</button>
                    <button 
                        onClick={handleAssign} 
                        disabled={
                            isSaving || 
                            isUploading || 
                            !selectedClassId ||
                            (!assignToAll && selectedStudentIds.length === 0) ||
                            (type === 'homework' && !selectedHomeworkId) ||
                            ((type === 'task' || type === 'announcement') && !title.trim()) ||
                            (type === 'announcement' && !instructions.trim()) ||
                            (type !== 'announcement' && !dueDate)
                        } 
                        className="flex items-center gap-2 px-8 py-2 bg-teal-600 text-white font-bold text-sm rounded-xl hover:bg-teal-700 shadow-lg shadow-teal-100 active:scale-95 disabled:opacity-50 transition-all"
                    >
                        {(isSaving || isUploading) && <Spinner className="h-4 w-4" />}
                        <span>{isUploading ? 'Đang tải tệp...' : (editingAssignment ? 'Cập nhật' : (resourceInfo?.type === 'video' ? 'Giao video ngay' : (type === 'homework' ? 'Giao bài ngay' : type === 'task' ? 'Giao nhiệm vụ' : 'Đăng thông báo')))}</span>
                    </button>
                </div>

                {zoomImage && (
                    <div 
                        className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300"
                        onClick={() => setZoomImage(null)}
                    >
                        <button className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all">
                            <XMarkIcon className="h-8 w-8" />
                        </button>
                        <img 
                            src={zoomImage} 
                            alt="Xem trước" 
                            className="max-w-full max-h-[90vh] rounded-2xl shadow-2xl object-contain animate-in zoom-in duration-300" 
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
